// /app/frontend/src/components/frugal-v2/components/CollapsibleSection.jsx
// Reusable collapsible section - 2 line header layout (130 lines)

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, X, AlertTriangle } from 'lucide-react';

/**
 * CollapsibleSection - Expandable container with 2-line header
 * Line 1: Icon + Title | Value (aligned right)
 * Line 2: Subtitle (small text, aligned right)
 * 
 * Uses ref to preserve expanded state across re-renders
 */
const CollapsibleSection = ({
  icon: Icon,
  title,
  total,
  subtitle,
  iconColorClass = 'text-emerald-400',
  children,
  addForm,
  showAddForm,
  onToggleAddForm,
  defaultExpanded = false,
  hasAlert = false,
  fundingStatus = null,
  t
}) => {
  // Use ref to track if user has manually toggled
  const userToggledRef = useRef(false);
  const [isListExpanded, setIsListExpanded] = useState(defaultExpanded);

  // Only reset to default if user hasn't manually toggled
  useEffect(() => {
    if (!userToggledRef.current) {
      setIsListExpanded(defaultExpanded);
    }
  }, [defaultExpanded]);

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (onToggleAddForm) onToggleAddForm();
  };

  const handleChevronClick = () => {
    userToggledRef.current = true; // Mark as manually toggled
    setIsListExpanded(!isListExpanded);
  };

  // Funding status colors
  const fundingBorder = fundingStatus === 'funded' ? 'border-emerald-500/40'
    : fundingStatus === 'partial' ? 'border-amber-500/40'
    : null;
  const fundingTitleClass = fundingStatus === 'funded' ? 'text-emerald-400'
    : fundingStatus === 'partial' ? 'text-amber-400'
    : fundingStatus === 'none' ? 'text-slate-400'
    : null;

  return (
    <div className={`rounded-xl border overflow-visible ${
      hasAlert 
        ? 'bg-red-500/10 border-red-500/30' 
        : fundingBorder
          ? `bg-white/5 ${fundingBorder}`
          : 'bg-white/5 border-white/10'
    }`}>
      {/* Header - Two lines */}
      <div className="p-4">
        {/* Line 1: Icon + Title + Add | Value + Chevron */}
        <div className="flex items-center justify-between">
          {/* Left: Icon + Title + Add Button + Alert */}
          <div className="flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 ${hasAlert ? 'text-red-400' : fundingTitleClass || iconColorClass}`} />}
            <h3 className={`${hasAlert ? 'text-red-400' : fundingTitleClass || iconColorClass} font-semibold`}>{title}</h3>
            
            {hasAlert && (
              <AlertTriangle className="w-4 h-4 text-red-400" />
            )}
            
            {/* Add Button */}
            {onToggleAddForm && (
              <button
                onClick={handleAddClick}
                className={`ml-1 w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                  showAddForm 
                    ? 'bg-white/20 text-white' 
                    : `${iconColorClass} bg-white/10 hover:bg-white/20`
                }`}
                title={showAddForm ? 'Cancel' : 'Add'}
              >
                {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
            )}
          </div>

          {/* Right: Value + Chevron */}
          <button
            onClick={handleChevronClick}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <span className={`${hasAlert ? 'text-red-400' : iconColorClass} font-bold text-lg`}>
              ${total?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
            </span>
            <ChevronDown 
              className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
                isListExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>

        {/* Line 2: Subtitle aligned right */}
        {subtitle && (
          <div className="flex justify-end mt-1">
            <span className="text-xs text-slate-400">
              {subtitle}
            </span>
          </div>
        )}
      </div>

      {/* Add Form - INDEPENDENT of list collapse */}
      {showAddForm && addForm && (
        <div className="px-4 pb-3 border-b border-white/10">
          {addForm}
        </div>
      )}

      {/* List Content - Controlled by chevron - scrollable when content overflows */}
      <div
        className={`transition-all duration-200 ease-in-out ${
          isListExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="px-4 pb-4 max-h-[480px] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
